import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { resultadoDaConferencia, type ResultadoDaConferencia } from '../lib/senha'
import { mensagemDaTroca, mensagemDoPedido } from './recuperacaoDeSenha'
import { mensagemDeCadastroFechado } from './assinatura'

interface ContextoAuth {
  session: Session | null
  carregando: boolean
  entrar: (email: string, senha: string) => Promise<string | null>
  cadastrar: (nome: string, email: string, senha: string) => Promise<string | null>
  pedirLinkDeNovaSenha: (email: string) => Promise<string | null>
  salvarNovaSenha: (senha: string) => Promise<string | null>
  conferirSenhaAtual: (senha: string) => Promise<ResultadoDaConferencia>
  atualizarPerfil: (dados: DadosDePerfil) => Promise<string | null>
  sair: () => Promise<void>
}

// Pedido dela em 09/09: o PDF so mostrava o e-mail de LOGIN, sem telefone.
// Mora em user_metadata -- mesmo lugar onde `nome` ja mora desde o cadastro
// (nao existe tabela de perfil separada) -- ver nomeDeExibicao.ts.
export interface DadosDePerfil {
  nome: string
  email_comercial: string
  telefone: string
}

const Contexto = createContext<ContextoAuth | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setCarregando(false)
    })
    const { data: inscricao } = supabase.auth.onAuthStateChange((_evento, novaSession) => {
      setSession(novaSession)
    })
    return () => inscricao.subscription.unsubscribe()
  }, [])

  async function entrar(email: string, senha: string): Promise<string | null> {
    const { error } = await supabase.auth.signInWithPassword({ email, password: senha })
    if (!error) return null
    if (error.message.includes('Invalid login credentials')) return 'E-mail ou senha incorretos.'
    return 'Não foi possível entrar. Verifique sua conexão e tente novamente.'
  }

  async function cadastrar(nome: string, email: string, senha: string): Promise<string | null> {
    const { error } = await supabase.auth.signUp({
      email,
      password: senha,
      options: { data: { nome } },
    })
    if (!error) return null
    if (error.message.includes('already registered')) return 'Este e-mail já está cadastrado.'
    const fechado = mensagemDeCadastroFechado(error)
    if (fechado) return fechado
    return 'Não foi possível criar a conta. Tente novamente.'
  }

  async function pedirLinkDeNovaSenha(email: string): Promise<string | null> {
    // O endereco de volta e o desta mesma instalacao (origin), e nao um
    // endereco fixo: assim o link funciona igual no app publicado e no
    // navegador de teste. O Supabase so aceita enderecos que estejam na lista
    // de Redirect URLs do painel.
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/nova-senha`,
    })
    return mensagemDoPedido(error)
  }

  // Decisao dela 06/10: o Trocar senha do Meu perfil confere a senha atual
  // entrando de novo com ela (mesmo usuario, a sessao so e renovada).
  async function conferirSenhaAtual(senha: string): Promise<ResultadoDaConferencia> {
    const email = session?.user.email
    if (!email) return 'sem-conexao'
    const { error } = await supabase.auth.signInWithPassword({ email, password: senha })
    return resultadoDaConferencia(error)
  }

  async function salvarNovaSenha(senha: string): Promise<string | null> {
    const { error } = await supabase.auth.updateUser({ password: senha })
    return mensagemDaTroca(error)
  }

  // `data` faz merge raso com o user_metadata existente (documentado pelo
  // Supabase) -- mas mando os TRES campos sempre, nunca so o que mudou, pra
  // nao depender desse detalhe de implementacao continuar valendo.
  async function atualizarPerfil(dados: DadosDePerfil): Promise<string | null> {
    const { error } = await supabase.auth.updateUser({ data: dados })
    if (!error) return null
    return 'Não foi possível salvar o perfil. Tente novamente.'
  }

  async function sair(): Promise<void> {
    await supabase.auth.signOut()
  }

  return (
    <Contexto.Provider
      value={{
        session,
        carregando,
        entrar,
        cadastrar,
        pedirLinkDeNovaSenha,
        salvarNovaSenha,
        conferirSenhaAtual,
        atualizarPerfil,
        sair,
      }}
    >
      {children}
    </Contexto.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): ContextoAuth {
  const contexto = useContext(Contexto)
  if (!contexto) throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  return contexto
}
